import { useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const testimonials = [
  {
    id: 1,
    name: "Ricardo Mendes",
    role: "CEO",
    company: "TechFlow Digital",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
    rating: 5,
    quote: "Migramos de uma solução instável que nos custava R$600/mês. Com a API Oficial pela Zavvi, reduzimos 45% dos custos e nunca mais tivemos um único banimento.",
    metric: "45% de redução de custos",
  },
  {
    id: 2,
    name: "Ana Carolina Reis",
    role: "Gestora de Atendimento",
    company: "Clínica Vida Plena",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
    rating: 5,
    quote: "Antes usávamos 3 ferramentas diferentes. Com a Zavvi, centralizamos tudo. Em 2 semanas, nosso tempo de resposta caiu de 40 minutos para menos de 2 minutos.",
    metric: "-95% no tempo de resposta",
  },
  {
    id: 3,
    name: "Felipe Drummond",
    role: "Diretor Comercial",
    company: "Imobiliária Ápex",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    rating: 5,
    quote: "A API Oficial foi um divisor de águas. Nunca mais tivemos número banido. E o suporte em português fez toda a diferença no setup.",
    metric: "Zero banimentos em 8 meses",
  },
  {
    id: 4,
    name: "Mariana Souza",
    role: "COO",
    company: "EduTech FastLearn",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
    rating: 5,
    quote: "Escalamos de 200 para 1.500 atendimentos/dia sem contratar nenhum agente a mais. O chatbot com IA resolve sozinho 60% dos casos.",
    metric: "7,5x mais atendimentos sem aumentar equipe",
  },
  {
    id: 5,
    name: "Amanda Costa",
    role: "Head de Atendimento",
    company: "E-commerce Plus",
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face",
    rating: 5,
    quote: "A IA integrada à Zavvi automatizou 70% dos nossos atendimentos de primeiro contato. Nossa equipe agora foca no que importa: fechar vendas.",
    metric: "70% de automação",
  },
];

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section id="depoimentos" className="py-16 md:py-24 relative overflow-hidden" aria-labelledby="testimonials-heading">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface-card/30 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-16"
        >
          <p className="text-cyber-cyan font-medium mb-3 md:mb-4 text-sm">PROVA SOCIAL</p>
          <h2 id="testimonials-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6">
            O que{" "}
            <span className="text-gradient-zavvi">nossos clientes dizem</span>
          </h2>
          <p className="text-base md:text-lg text-body-text px-4">
            4,9/5 de satisfação média em +500 empresas que escalam com a Zavvi.
          </p>
        </motion.div>

        {/* Featured Testimonial */}
        <div className="max-w-4xl mx-auto mb-10 md:mb-16">
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="glass-card rounded-2xl md:rounded-3xl p-6 md:p-10 neon-border-purple relative"
              >
                {/* Quote Icon */}
                <div className="absolute -top-3 md:-top-4 left-6 md:left-8 w-8 md:w-12 h-8 md:h-12 rounded-full bg-gradient-zavvi flex items-center justify-center glow-gradient">
                  <Quote size={16} className="md:w-6 md:h-6 text-foreground" />
                </div>

                <div className="pt-4 md:pt-0">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4 md:mb-6">
                    {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                      <Star key={i} size={16} className="md:w-5 md:h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-base md:text-xl text-foreground leading-relaxed mb-4 md:mb-6">
                    "{testimonials[activeIndex].quote}"
                  </blockquote>

                  {/* Metric Badge */}
                  <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-success-green/10 border border-success-green/30 mb-4 md:mb-6">
                    <span className="text-success-green font-bold text-sm md:text-base">
                      📈 {testimonials[activeIndex].metric}
                    </span>
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-3 md:gap-4">
                    <img
                      src={testimonials[activeIndex].avatar}
                      alt={`Foto de ${testimonials[activeIndex].name}, ${testimonials[activeIndex].role} na ${testimonials[activeIndex].company} - cliente Zavvi.ai`}
                      className="w-10 md:w-14 h-10 md:h-14 rounded-full object-cover ring-2 ring-electric-purple/50"
                      width={56}
                      height={56}
                      loading="lazy"
                    />
                    <div>
                      <div className="font-bold text-foreground text-sm md:text-base">
                        {testimonials[activeIndex].name}
                      </div>
                      <div className="text-muted-foreground text-xs md:text-sm">
                        {testimonials[activeIndex].role} • {testimonials[activeIndex].company}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 w-10 md:w-12 h-10 md:h-12 rounded-full glass-card flex items-center justify-center text-foreground hover:bg-muted transition-colors z-10"
            >
              <ChevronLeft size={20} className="md:w-6 md:h-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 w-10 md:w-12 h-10 md:h-12 rounded-full glass-card flex items-center justify-center text-foreground hover:bg-muted transition-colors z-10"
            >
              <ChevronRight size={20} className="md:w-6 md:h-6" />
            </button>
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-6 md:mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setActiveIndex(index);
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? "w-6 md:w-8 bg-gradient-zavvi"
                    : "w-2 bg-muted hover:bg-muted-foreground"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
        >
          {[
            { value: "500+", label: "Empresas Ativas" },
            { value: "2M+", label: "Mensagens/Mês" },
            { value: "4.9/5", label: "Avaliação Média" },
            { value: "< 2min", label: "Tempo de Resposta" },
          ].map((stat) => (
            <div key={stat.label} className="text-center glass-card p-4 md:p-6 rounded-xl">
              <div className="text-2xl md:text-4xl font-display font-bold text-gradient-zavvi mb-1 md:mb-2">
                {stat.value}
              </div>
              <div className="text-muted-foreground text-xs md:text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
